import { Badge, ListItem, ListItemButton, ListItemDecorator, Tooltip, Typography } from '@mui/joy';
import { createContext, useContext } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router';

import { AppContext } from '@/shared/context/context';

type DecoratorProps = React.PropsWithChildren<{
  active?: boolean;
}>;

const SidebarDecorator: React.FC<DecoratorProps> = ({ active = false, children }) => (
  <ListItemDecorator className="mr-[-10px]">
    <Typography className="text-xs" component="span" textColor={active ? 'primary.600' : 'inherit'}>
      {children}
    </Typography>
  </ListItemDecorator>
);

export const SidebarFlyoutContext = createContext(false);

export type SidebarItemProps = {
  badgeContent?: string | number;
  badgeTooltip?: string;
  className?: string;
  disableCollapsedTooltip?: boolean;
  endAction?: React.ReactNode;
  icon?: React.ReactNode;
  nested?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLLIElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLLIElement>) => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLLIElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLLIElement>) => void;
  prefixMatch?: boolean;
  route: string;
  title: string;
  toolTip?: string;
};

const SidebarItem: React.FC<SidebarItemProps> = ({
  badgeContent,
  badgeTooltip,
  className,
  disableCollapsedTooltip = false,
  endAction,
  icon,
  nested = false,
  onBlur,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  prefixMatch = false,
  route,
  title,
  toolTip = '',
}) => {
  const { ui } = useContext(AppContext);
  const inFlyout = useContext(SidebarFlyoutContext);

  const location = useLocation();

  const active = (() => {
    const currentPathName = location.pathname.toLowerCase();
    const currentRoute = route.toLowerCase();

    return prefixMatch
      ? currentPathName.startsWith(currentRoute)
      : currentPathName === currentRoute;
  })();

  const expanded = ui.toggles.mainMenu || inFlyout;
  const iconDecorator = icon && <SidebarDecorator active={active}>{icon}</SidebarDecorator>;

  const content = (() => {
    if (expanded) {
      return (
        <>
          {iconDecorator}
          <Tooltip followCursor title={toolTip}>
            <Typography
              level={nested ? 'inherit' : 'body-sm'}
              textColor={active ? 'primary.600' : 'text.primary'}
            >
              <span className="d-block max-w-[122px] overflow-hidden truncate text-ellipsis">
                {title}
              </span>
            </Typography>
          </Tooltip>
        </>
      );
    }

    if (disableCollapsedTooltip) return iconDecorator;

    return (
      <Tooltip arrow placement="right" title={title}>
        <span>{iconDecorator}</span>
      </Tooltip>
    );
  })();

  return (
    <ListItem
      endAction={endAction}
      onBlur={onBlur}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <ListItemButton
        className={classNames({ 'py-1 pl-8': nested }, { 'py-1.5 pl-5': !nested }, className)}
        component={Link}
        to={route}
      >
        <Badge
          badgeContent={
            badgeContent && (
              <Tooltip title={badgeTooltip}>
                <span>{badgeContent}</span>
              </Tooltip>
            )
          }
          badgeInset="-10%"
          invisible={!badgeContent}
          size="sm"
        >
          {content}
        </Badge>
      </ListItemButton>
    </ListItem>
  );
};

export default SidebarItem;
